import { DTFItem, Planche } from './packing';
import { savePendingOrder } from './persist';
import { DTFSettings } from './settings';

/**
 * FEATURE_DOWNLOAD_TEST — bouton « Télécharger » demandé par Daniel pour VALIDER le rendu
 * (vectoriel conservé, dimensions réelles). Outil de TEST uniquement : le fichier de
 * production sera généré côté serveur. À désactiver ou conditionner en Phase 3 (crédits,
 * règle anti-concurrent).
 */
// #223 : aucun export avant commande pour les clients (décision Daniel 08.09.2026).
// #224 (Daniel 22.09.2026) : Daniel veut pouvoir télécharger les PDF pour son propre usage.
// Daniel 07.10.2026 : le bouton « Tout télécharger (PDF) » est réservé aux comptes ADMIN du site (profiles.is_admin, vérifié par
// /api/me avec le JWT Supabase du site, même origine). Les clients ne le voient jamais. Le paramètre ?telecharger=1 ne vaut plus
// que sur localhost/127.0.0.1 (tests e2e). Aucune mémorisation navigateur ; l'ancien drapeau localStorage est effacé.
export function isDownloadUnlocked(): boolean {
	try {
		localStorage.removeItem('dtf_v2_download');
		const local = /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
		return local && new URLSearchParams(window.location.search).get('telecharger') === '1';
	} catch {
		return false;
	}
}
/** JWT Supabase du site (même origine : clé localStorage sb-<ref>-auth-token posée par header.js). */
export function siteAccessToken(): string {
	try {
		for (let i = 0; i < localStorage.length; i++) {
			const k = localStorage.key(i) || '';
			if (!/^sb-.*-auth-token$/.test(k)) continue;
			const v = JSON.parse(localStorage.getItem(k) || 'null');
			const t = v && (v.access_token || (v.currentSession && v.currentSession.access_token));
			if (t) return String(t);
		}
	} catch {
		/* pas de session */
	}
	return '';
}
/** Clé anonyme publique du projet Supabase du site (la même que dans assets/header.js) : sert uniquement à rafraîchir la session. */
const SB_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imhvcmhyd3d2Y3loZ2hldndjaG9oIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTE4MDcyMjQsImV4cCI6MjA2NzM4MzIyNH0.Du03K6zUekXMOtiX8o1VzAuQQrMVF9t-xuWDY2AFbaA';
let refreshing: Promise<string> | null = null;
/**
 * Session du site rafraîchie si elle expire (Daniel 15.10.2026 : « connecté en tant qu'admin, plus de bouton PDF »). L'éditeur n'embarque pas
 * supabase-js : le jeton posé par header.js expire au bout d'une heure et n'était jamais renouvelé ici → /api/me et la bibliothèque répondaient
 * « session expirée ». On renouvelle avec le refresh_token et on réécrit la session au même endroit (le site la reprend telle quelle).
 */
export async function refreshSiteSession(): Promise<string> {
	if (refreshing) return refreshing;
	refreshing = (async () => {
		try {
			for (let i = 0; i < localStorage.length; i++) {
				const k = localStorage.key(i) || '';
				const m = /^sb-([a-z0-9]+)-auth-token$/.exec(k);
				if (!m) continue;
				const v = JSON.parse(localStorage.getItem(k) || 'null');
				const sess = v && v.currentSession ? v.currentSession : v;
				if (!sess || !sess.access_token) continue;
				const exp = Number(sess.expires_at || 0);
				if (!exp || exp * 1000 > Date.now() + 90_000 || !sess.refresh_token) return String(sess.access_token);
				const r = await fetch(`https://${m[1]}.supabase.co/auth/v1/token?grant_type=refresh_token`, {
					method: 'POST',
					headers: { apikey: SB_ANON_KEY, 'Content-Type': 'application/json' },
					body: JSON.stringify({ refresh_token: sess.refresh_token }),
				});
				if (!r.ok) return String(sess.access_token);
				const j = await r.json();
				if (!j || !j.access_token) return String(sess.access_token);
				const next = { ...sess, access_token: j.access_token, refresh_token: j.refresh_token || sess.refresh_token, expires_in: j.expires_in, expires_at: j.expires_at || Math.floor(Date.now() / 1000) + Number(j.expires_in || 3600), token_type: 'bearer', user: j.user || sess.user };
				localStorage.setItem(k, JSON.stringify(v && v.currentSession ? { ...v, currentSession: next } : next));
				return String(j.access_token);
			}
		} catch {
			/* session illisible */
		}
		return siteAccessToken();
	})();
	try {
		return await refreshing;
	} finally {
		refreshing = null;
	}
}

/** true si le compte connecté sur le site est administrateur (sinon false, silencieusement). */
export async function fetchAdminDownload(): Promise<boolean> {
	const t = await refreshSiteSession();
	if (!t) return false;
	try {
		const r = await fetch(`${window.location.origin}/api/me`, { headers: { Authorization: `Bearer ${t}` }, cache: 'no-store' });
		if (!r.ok) return false;
		const j = await r.json();
		return !!(j && j.admin === true);
	} catch {
		return false;
	}
}
export const FEATURE_DOWNLOAD_TEST = isDownloadUnlocked();

const esc = (s: string) =>
	s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Suffixe les id internes d'un SVG pour éviter les collisions entre logos dans le fichier final. */
function namespaceSvgIds(svgText: string, suffix: string): string {
	const ids = new Set<string>();
	svgText.replace(/\sid="([^"]+)"/g, (_m, id) => {
		ids.add(id);
		return _m;
	});
	let out = svgText;
	for (const id of ids) {
		const safe = id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
		out = out
			.replace(new RegExp(`(\\sid=")${safe}(")`, 'g'), `$1${id}${suffix}$2`)
			.replace(new RegExp(`(url\\(['"]?#)${safe}(['"]?\\))`, 'g'), `$1${id}${suffix}$2`)
			.replace(new RegExp(`(href\\s*=\\s*"#)${safe}(")`, 'g'), `$1${id}${suffix}$2`);
	}
	return out;
}

/** Positionne un SVG d'origine (intact) aux coordonnées cibles, en mm. */
function placeSvg(svgText: string, xMm: number, yMm: number, wMm: number, hMm: number, idx: number): string {
	const namespaced = namespaceSvgIds(svgText, `-l${idx}`);
	return namespaced.replace(
		/<svg\b([^>]*)>/i,
		(_m, attrs: string) => {
			const cleaned = attrs
				.replace(/\s(x|y|width|height|preserveAspectRatio)="[^"]*"/gi, '')
				.replace(/\s(x|y|width|height|preserveAspectRatio)='[^']*'/gi, '');
			return `<svg${cleaned} x="${xMm}" y="${yMm}" width="${wMm}" height="${hMm}" preserveAspectRatio="none">`;
		},
	);
}

/**
 * Rasters → ré-encodés par le NAVIGATEUR avant le PDF (Daniel 29.09.2026 : « certains logos ne sont
 * pas dans la planche »). svg2pdf passe la data URL d'origine telle quelle à jsPDF, dont le décodeur
 * PNG refuse certains fichiers (PNG entrelacé Adam7 prouvé sur banc ; GIF/PNG imbriqués perdent
 * l'alpha) — et dans ce cas il se contente d'un console.warn : le logo était omis EN SILENCE.
 * Ici chaque raster passe par un canvas (décodeur du navigateur, orientation EXIF comprise) :
 * PNG sans perte (alpha conservé) ; un JPEG d'origine est gardé tel quel (jsPDF le lit bien et une
 * recompression abîmerait les contours), sauf s'il porte une rotation EXIF que jsPDF ignorerait.
 * Résolution : celle du fichier, plafonnée à 12 px/mm (≈ 305 dpi) de la taille imprimée et à
 * 12 Mpx (limite canvas Safari). Les logos SVG restent vectoriels ; seules leurs <image> internes
 * en data URL sont ré-encodées.
 */
const PDF_PX_PER_MM = 12;
const PDF_MAX_PIXELS = 12_000_000;
export class PlancheImageError extends Error {
	constructor(public itemName: string) {
		super(`image illisible : ${itemName}`);
		this.name = 'PlancheImageError';
	}
}
const loadImage = (src: string): Promise<HTMLImageElement> =>
	new Promise((resolve, reject) => {
		const im = new Image();
		im.onload = () => resolve(im);
		im.onerror = () => reject(new Error('image load failed'));
		im.src = src;
	});
/** Orientation EXIF d'un JPEG (1 = aucune). Lecture directe des octets : le navigateur applique
 * l'EXIF à l'affichage, jsPDF non → un tel logo sortirait couché ou à l'envers. */
function jpegExifOrientation(dataUrl: string): number {
	try {
		const b64 = dataUrl.slice(dataUrl.indexOf(',') + 1, dataUrl.indexOf(',') + 1 + 180_000);
		const bin = atob(b64.slice(0, b64.length - (b64.length % 4)));
		const u = new Uint8Array(bin.length);
		for (let i = 0; i < bin.length; i++) u[i] = bin.charCodeAt(i);
		if (u[0] !== 0xff || u[1] !== 0xd8) return 1;
		let off = 2;
		while (off + 4 <= u.length) {
			if (u[off] !== 0xff) return 1;
			const marker = u[off + 1];
			if (marker === 0xda || marker === 0xd9) return 1;
			const size = (u[off + 2] << 8) | u[off + 3];
			const end = off + 2 + size;
			if (marker === 0xe1) {
				if (size < 16 || end > u.length) return 1;
				const exif = u[off + 4] === 0x45 && u[off + 5] === 0x78 && u[off + 6] === 0x69 && u[off + 7] === 0x66 && u[off + 8] === 0 && u[off + 9] === 0;
				if (!exif) {
					off = end;
					continue;
				}
				const t = off + 10;
				const le = u[t] === 0x49 && u[t + 1] === 0x49 ? true : u[t] === 0x4d && u[t + 1] === 0x4d ? false : null;
				if (le === null) return 1;
				const r16 = (i: number) => (le ? u[i] | (u[i + 1] << 8) : (u[i] << 8) | u[i + 1]);
				const r32 = (i: number) =>
					le ? (u[i] | (u[i + 1] << 8) | (u[i + 2] << 16) | (u[i + 3] << 24)) >>> 0 : ((u[i] << 24) | (u[i + 1] << 16) | (u[i + 2] << 8) | u[i + 3]) >>> 0;
				if (r16(t + 2) !== 42) return 1;
				const rel = r32(t + 4);
				const ifd = t + rel;
				if (rel < 8 || ifd + 2 > end) return 1;
				const n = r16(ifd);
				if (n > Math.floor((end - ifd - 2) / 12)) return 1;
				for (let i = 0; i < n; i++) {
					const e = ifd + 2 + i * 12;
					if (r16(e) !== 0x0112) continue;
					if (r16(e + 2) !== 3 || r32(e + 4) !== 1) return 1;
					const o = r16(e + 8);
					return o >= 1 && o <= 8 ? o : 1;
				}
				return 1;
			}
			off = end;
		}
	} catch (_e) {
		/* octets illisibles : on considère qu'il n'y a pas de rotation */
	}
	return 1;
}
async function reencodeRaster(src: string, wMm: number, hMm: number, allowUpscale = false): Promise<string> {
	const im = await loadImage(src);
	const nw = im.naturalWidth;
	const nh = im.naturalHeight;
	if (!nw || !nh) throw new Error('image vide');
	// revue Neo 29.09 : un JPEG d'origine passe bien dans jsPDF, le recompresser dégraderait
	// contours et petits textes → on le garde tel quel, sauf s'il porte une rotation EXIF
	if (/^data:image\/jpe?g[;,]/i.test(src) && jpegExifOrientation(src) === 1) return src;
	let scale = Math.max((wMm * PDF_PX_PER_MM) / nw, (hMm * PDF_PX_PER_MM) / nh);
	if (!allowUpscale) scale = Math.min(1, scale);
	if (!Number.isFinite(scale) || scale <= 0) scale = 1;
	scale = Math.min(scale, Math.sqrt(PDF_MAX_PIXELS / (nw * nh)));
	const W = Math.max(1, Math.round(nw * scale));
	const H = Math.max(1, Math.round(nh * scale));
	const canvas = document.createElement('canvas');
	canvas.width = W;
	canvas.height = H;
	const ctx = canvas.getContext('2d');
	if (!ctx) throw new Error('canvas indisponible');
	ctx.drawImage(im, 0, 0, W, H);
	const out = canvas.toDataURL('image/png');
	canvas.width = 1;
	canvas.height = 1;
	if (!out.startsWith('data:image/png;base64,')) throw new Error('canvas.toDataURL vide');
	return out;
}
/** <image href="data:…"> à l'intérieur d'un logo SVG : ré-encodées à leur résolution native (plafond 12 Mpx). */
async function reencodeSvgImages(svgText: string): Promise<string> {
	const re = /((?:xlink:)?href=")(data:image\/[^"]+)(")/g;
	const found = new Set<string>();
	svgText.replace(re, (m, _a, uri) => {
		found.add(uri);
		return m;
	});
	if (!found.size) return svgText;
	const map = new Map<string, string>();
	for (const uri of found) {
		if (/^data:image\/svg\+xml/i.test(uri)) continue;
		map.set(uri, await reencodeRaster(uri, Infinity, Infinity));
	}
	return svgText.replace(re, (m, a, uri, b) => (map.has(uri) ? `${a}${map.get(uri)}${b}` : m));
}
/** Copie des items de la planche avec des sources que jsPDF sait décoder. Le cache évite de refaire le travail planche après planche. */
export type PdfPrepCache = Map<string, Promise<DTFItem>>;
export const newPdfPrepCache = (): PdfPrepCache => new Map();
async function prepareItemsForPdf(planche: Planche, items: DTFItem[], cache: PdfPrepCache): Promise<DTFItem[]> {
	const needed = new Set(planche.placements.map(p => p.itemId));
	const out: DTFItem[] = [];
	for (const it of items) {
		if (!needed.has(it.id)) {
			out.push(it);
			continue;
		}
		let job = cache.get(it.id);
		if (!job) {
			job = (async () => {
				try {
					if (it.kind === 'svg' && it.svgText) {
						// image externe (http/blob) : jamais intégrée par svg2pdf → on refuse net
						if (/(?:xlink:)?href\s*=\s*["'](?:https?:|blob:|\/\/)/i.test(it.svgText)) throw new Error('image externe');
						// <mask> / <filter> : svg2pdf les rend faux ou les ignore → le logo est rastérisé
						// par le navigateur à 12 px/mm (le vectoriel est perdu, mais le rendu est juste)
						if (/<(mask|filter)[\s>]/i.test(it.svgText))
							return { ...it, kind: 'raster', svgText: undefined, src: await reencodeRaster(it.src, it.widthCm * 10, it.heightCm * 10, true) };
						return { ...it, svgText: await reencodeSvgImages(it.svgText) };
					}
					return { ...it, src: await reencodeRaster(it.src, it.widthCm * 10, it.heightCm * 10) };
				} catch (_e) {
					throw new PlancheImageError(it.name);
				}
			})();
			cache.set(it.id, job);
		}
		out.push(await job);
	}
	return out;
}
/** Garde-fou : svg2pdf n'échoue jamais sur une image, il l'omet avec un console.warn. On refuse la planche dans ce cas. */
let pdfQueue: Promise<unknown> = Promise.resolve();
async function svgToPdfStrict(doc: { svg: (el: Element, o: object) => Promise<unknown> }, el: Element, wMm: number, hMm: number): Promise<void> {
	// un seul doc.svg() à la fois : le hook console.warn est global
	const run: Promise<void> = pdfQueue.then(() => svgToPdfStrictInner(doc, el, wMm, hMm));
	pdfQueue = run.catch((): void => undefined);
	return run;
}
async function svgToPdfStrictInner(doc: { svg: (el: Element, o: object) => Promise<unknown> }, el: Element, wMm: number, hMm: number): Promise<void> {
	const orig = console.warn;
	const lost: string[] = [];
	console.warn = (...a: unknown[]) => {
		const msg = String(a[0] ?? '');
		if (msg.startsWith('Could not load image')) lost.push(msg);
		else orig.apply(console, a);
	};
	try {
		await doc.svg(el, { x: 0, y: 0, width: wMm, height: hMm });
	} finally {
		console.warn = orig;
	}
	if (lost.length) throw new PlancheImageError(`${lost.length} image(s)`);
}

/**
 * Planche maître en SVG à l'échelle réelle : 1 unité utilisateur = 1 mm.
 * Les logos vectoriels (SVG) sont replacés en VECTEURS d'origine ; les rasters/PDF en <image>.
 */
export function buildPlancheSvg(planche: Planche, items: DTFItem[], s: DTFSettings): string {
	const byId = new Map(items.map(it => [it.id, it]));
	const wMm = s.laizeCm * 10;
	const hMm = Math.round(planche.usedCm * 10 * 10) / 10;
	if (!(wMm > 0) || !(hMm > 0)) throw new PlancheImageError('planche vide');
	const parts: string[] = [];
	planche.placements.forEach((p, i) => {
		const item = byId.get(p.itemId);
		// revue Neo 29.09 : un placement orphelin ou sans dimensions faisait un trou silencieux
		if (!item) throw new PlancheImageError(`élément ${p.itemId} introuvable`);
		if (!(p.wCm > 0) || !(p.hCm > 0) || !(item.widthCm > 0) || !(item.heightCm > 0)) throw new PlancheImageError(item.name);
		if (![p.xCm, p.yCm, p.rotDeg || 0].every(Number.isFinite)) throw new PlancheImageError(item.name);
		const rot = p.rotDeg || 0;
		// p.wCm/hCm = bbox pivoté (nesting) ; le logo garde ses dims réelles, centré + tourné dedans
		const bx = p.xCm * 10;
		const by = p.yCm * 10;
		const bw = p.wCm * 10;
		const bh = p.hCm * 10;
		const w = rot ? item.widthCm * 10 : bw;
		const h = rot ? item.heightCm * 10 : bh;
		const x = rot ? 0 : bx;
		const y = rot ? 0 : by;
		const inner =
			item.kind === 'svg' && item.svgText
				? placeSvg(item.svgText, x, y, w, h, i)
				: `<image x="${x}" y="${y}" width="${w}" height="${h}" preserveAspectRatio="none" href="${esc(item.src)}"/>`;
		parts.push(
			rot
				? `<g transform="translate(${bx + bw / 2} ${by + bh / 2}) rotate(${rot}) translate(${-w / 2} ${-h / 2})">${inner}</g>`
				: inner,
		);
	});
	return (
		`<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" ` +
		`width="${wMm}mm" height="${hMm}mm" viewBox="0 0 ${wMm} ${hMm}">\n` +
		parts.join('\n') +
		`\n</svg>`
	);
}

function downloadBlob(blob: Blob, filename: string): void {
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	a.remove();
	setTimeout(() => URL.revokeObjectURL(url), 10000);
}

/** Date du jour pour les noms de fichiers : JJ-MM-AAAA. */
function fileDate(): string {
	const d = new Date();
	const p = (n: number) => String(n).padStart(2, '0');
	return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${d.getFullYear()}`;
}

// Règle Daniel (2026-08-19) : le nom porte TOUJOURS le nombre d'impressions de la
// planche (« 5x ») + la date — l'imprimeur lit la quantité sans ouvrir l'éditeur.
// #224e (Daniel 23.09.2026) : nom de la préparation (client / commande) saisi dans l'éditeur, mémorisé
// dans ce navigateur (localStorage `dtf_v2_job_name`). Ordre voulu par Daniel : QUANTITÉ d'abord, puis le
// nom, puis « planche N », puis la date. Ex. : 2x_Sport-Times_planche-1_550x955mm_23-09-2026.pdf
export const JOB_NAME_KEY = 'dtf_v2_job_name';
export function getJobName(): string {
	try {
		return (localStorage.getItem(JOB_NAME_KEY) || '').trim();
	} catch {
		return '';
	}
}
export function setJobName(v: string): void {
	try {
		localStorage.setItem(JOB_NAME_KEY, v);
	} catch {
		/* stockage indisponible : le nom ne sera pas mémorisé */
	}
}
/** Nom sûr pour un fichier : espaces → tirets, caractères interdits retirés, 40 caractères max. */
const safeName = (v: string) =>
	v
		.normalize('NFKC')
		.replace(/[\\/:*?"<>|]+/g, '')
		.replace(/\s+/g, '-')
		.replace(/-+/g, '-')
		.replace(/^-|-$/g, '')
		.slice(0, 40);
const plancheFilename = (index: number, planche: Planche, s: DTFSettings, ext: string) => {
	const name = safeName(getJobName());
	return `${planche.qty}x_${name ? name + '_' : ''}planche-${index + 1}_${s.laizeCm * 10}x${Math.round(planche.usedCm * 10)}mm_${fileDate()}.${ext}`;
};

export function downloadPlancheSvg(planche: Planche, index: number, items: DTFItem[], s: DTFSettings): void {
	const svg = buildPlancheSvg(planche, items, s);
	downloadBlob(new Blob([svg], { type: 'image/svg+xml' }), plancheFilename(index, planche, s, 'svg'));
}

export async function buildPlanchePdf(
	planche: Planche,
	items: DTFItem[],
	s: DTFSettings,
	cache: PdfPrepCache = newPdfPrepCache(),
): Promise<ArrayBuffer> {
	const [{ jsPDF }] = await Promise.all([import('jspdf'), import('svg2pdf.js')]);
	const prepared = await prepareItemsForPdf(planche, items, cache);
	const wMm = s.laizeCm * 10;
	const hMm = Math.round(planche.usedCm * 10 * 10) / 10;
	const doc = new jsPDF({
		unit: 'mm',
		format: [wMm, hMm],
		orientation: wMm >= hMm ? 'landscape' : 'portrait',
		compress: true,
	});
	const svgStr = buildPlancheSvg(planche, prepared, s);
	const parsedDoc = new DOMParser().parseFromString(svgStr, 'image/svg+xml');
	if (parsedDoc.querySelector('parsererror')) throw new PlancheImageError('SVG de planche invalide');
	const el = parsedDoc.documentElement;
	await svgToPdfStrict(doc, el as unknown as Element, wMm, hMm);
	return doc.output('arraybuffer');
}

export async function downloadPlanchePdf(
	planche: Planche,
	index: number,
	items: DTFItem[],
	s: DTFSettings,
): Promise<void> {
	const buf = await buildPlanchePdf(planche, items, s);
	downloadBlob(new Blob([buf], { type: 'application/pdf' }), plancheFilename(index, planche, s, 'pdf'));
}

/**
 * Export global : un ZIP contenant UN PDF PAR PLANCHE (jamais un PDF fusionné),
 * chaque fichier nommé avec sa quantité d'impression + la date. Les logos
 * vectoriels restent vectoriels (même pipeline svg2pdf que l'export unitaire).
 */
export async function downloadAllPlanchesPdfZip(
	planches: Planche[],
	items: DTFItem[],
	s: DTFSettings,
): Promise<void> {
	const { zipSync } = await import('fflate');
	const entries: Record<string, Uint8Array> = {};
	const cache = newPdfPrepCache();
	for (let i = 0; i < planches.length; i++) {
		const buf = await buildPlanchePdf(planches[i], items, s, cache);
		// PDF déjà compressé par jsPDF → store (level 0), zip quasi instantané
		entries[plancheFilename(i, planches[i], s, 'pdf')] = new Uint8Array(buf);
	}
	const zipped = zipSync(entries, { level: 0 });
	const zipName = safeName(getJobName()) || 'planches-dtf';
	downloadBlob(new Blob([zipped], { type: 'application/zip' }), `${zipName}_${fileDate()}.zip`);
}

/**
 * #223 « Je passe ma commande » : fabrique un PDF par planche (jamais téléchargé),
 * le dépose dans IndexedDB et envoie le client sur la page de commande du site,
 * qui l'ajoute au panier comme un fichier déposé. Aucune sortie de fichier avant commande.
 */
export const ORDER_PAGE = '/commande-dtfs.html?src=editeur';
export async function handoffToOrder(planches: Planche[], items: DTFItem[], s: DTFSettings, glue: 'base' | 'anti'): Promise<void> {
	const out = [];
	const cache = newPdfPrepCache();
	for (let i = 0; i < planches.length; i++) {
		const p = planches[i];
		const pdf = await buildPlanchePdf(p, items, s, cache);
		out.push({
			name: `Planche-${i + 1}-editeur-DTFSwiss_${s.laizeCm}x${Math.round(p.usedCm)}cm_${p.qty}x_${fileDate()}.pdf`,
			pdf,
			wcm: s.laizeCm,
			hcm: Math.round(p.usedCm * 10) / 10,
			qty: p.qty,
			index: i,
			glue,
		});
	}
	await savePendingOrder({ createdAt: new Date().toISOString(), source: 'editeur-v2', planches: out, glue });
	window.location.href = ORDER_PAGE;
}
